// ────────────────────────────────────────────────────────────────────────
// WeddingPartyQuestionnaire — modal that gathers the bride's choices for
// the trio render (groom / groomsmen / bridesmaids). Wedding Trio rules:
//   • Groom keeps his real face
//   • Groomsmen + Bridesmaids are faceless dress-form mannequins
//   • Tie palette suggestions come back from the backend, derived from
//     the groom suit colour and the bridesmaids palette
// Designed to be opened from Dashboard.jsx ("Plan Wedding Party" button).
// All choices are passed up via `onSubmit({...})` — the parent owns the
// rest of the flow (API call, results display).
// ────────────────────────────────────────────────────────────────────────
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Loader2, Sparkles } from 'lucide-react';

// Curated suit colours — same set used elsewhere in the workspace
const SUIT_COLOR_OPTIONS = [
  { name: 'Black Tuxedo',      hex: '#0B0B0B' },
  { name: 'Midnight Navy',     hex: '#1C2841' },
  { name: 'Charcoal',          hex: '#36454F' },
  { name: 'Slate Grey',        hex: '#708090' },
  { name: 'Burgundy',          hex: '#722F37' },
  { name: 'Forest Green',      hex: '#2C4A3A' },
  { name: 'Tan',               hex: '#D2B48C' },
  { name: 'Ivory',             hex: '#F5F0E1' },
  { name: 'Dove Grey',         hex: '#9098A1' },
  { name: 'Chocolate Brown',   hex: '#3B2F2F' },
];

// Bridesmaids palette — wedding-industry common picks
const DRESS_COLOR_OPTIONS = [
  { name: 'Dusty Rose',        hex: '#C9A0A4' },
  { name: 'Sage',              hex: '#9CAF88' },
  { name: 'Burgundy',          hex: '#722F37' },
  { name: 'Navy',              hex: '#1C2841' },
  { name: 'Champagne',         hex: '#D4B996' },
  { name: 'Forest Green',      hex: '#2C4A3A' },
  { name: 'Blush Pink',        hex: '#E8C5C9' },
  { name: 'Dusty Blue',        hex: '#7DA0B3' },
  { name: 'Mauve',             hex: '#B79DA8' },
  { name: 'Emerald',           hex: '#066B44' },
  { name: 'Terracotta',        hex: '#C66B43' },
  { name: 'Lavender',          hex: '#B5A4C2' },
];

const GROOM_GARMENT_OPTIONS = [
  'Classic Tuxedo',
  'Three-Piece Suit',
  'Single-Breasted Two-Piece',
  'Double-Breasted Suit',
  'Morning Suit',
  'Mandarin-Collar Suit',
  'Dinner Jacket (Black Tie)',
  'White Tuxedo',
];

const GROOMSMEN_GARMENT_OPTIONS = [
  'Matching Single-Breasted Suits',
  'Matching Three-Piece Suits',
  'Matching Tuxedos',
  'Coordinated Sport Coats + Trousers',
  'Mismatched Same-Palette Suits',
];

const BRIDESMAIDS_GARMENT_OPTIONS = [
  'A-Line Floor-Length Dress',
  'Sheath Floor-Length Dress',
  'Fit-and-Flare Floor-Length Dress',
  'Mermaid Floor-Length Dress',
  'Tea-Length A-Line Dress',
  'Wrap-Style Floor-Length Dress',
];

const GROOM_STYLE_OPTIONS = [
  { value: 'formal',    label: 'Formal (church / cathedral)' },
  { value: 'cocktail',  label: 'Cocktail (semi-formal)' },
  { value: 'black_tie', label: 'Black Tie (evening / gala)' },
  { value: 'garden',    label: 'Garden / Outdoor (relaxed)' },
];

const WEDDING_PALETTE_THEMES = [
  { value: 'classic_white',  label: 'Classic White & Black',     hint: 'Black tie energy · all-time elegance' },
  { value: 'garden_spring',  label: 'Garden · Spring',            hint: 'Sage + champagne + pastel pinks' },
  { value: 'autumn_warm',    label: 'Warm Autumn',                hint: 'Burgundy + rust + forest + ivory' },
  { value: 'winter_jewel',   label: 'Winter Jewel',               hint: 'Emerald + midnight navy + ruby' },
  { value: 'coastal_summer', label: 'Coastal Summer',             hint: 'Dusty blue + ivory + sand' },
  { value: 'desert_neutral', label: 'Desert Neutral',             hint: 'Terracotta + tan + ivory' },
];

function PaletteSwatch({ option, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-2 px-3 py-2 border transition-all text-left ${selected ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10' : 'border-[#27272A] hover:border-[#52525B]'}`}
      data-testid={`palette-${option.name.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <span className="w-5 h-5 border border-[#27272A] flex-shrink-0" style={{ backgroundColor: option.hex }} />
      <span className="text-[11px] text-[#E4E4E7] truncate">{option.name}</span>
    </button>
  );
}

export default function WeddingPartyQuestionnaire({ open, onClose, onSubmit, generating }) {
  // Wedding theme / palette
  const [theme, setTheme] = useState('classic_white');
  const [season, setSeason] = useState('spring');

  // Groom
  const [groomColor, setGroomColor] = useState(SUIT_COLOR_OPTIONS[0]);   // Black Tuxedo
  const [groomGarment, setGroomGarment] = useState(GROOM_GARMENT_OPTIONS[0]);
  const [groomStyle, setGroomStyle] = useState('formal');

  // Groomsmen
  const [groomsmenColor, setGroomsmenColor] = useState(SUIT_COLOR_OPTIONS[2]);  // Charcoal
  const [groomsmenGarment, setGroomsmenGarment] = useState(GROOMSMEN_GARMENT_OPTIONS[0]);

  // Bridesmaids
  const [bridesmaidsColor, setBridesmaidsColor] = useState(DRESS_COLOR_OPTIONS[0]);  // Dusty Rose
  const [bridesmaidsGarment, setBridesmaidsGarment] = useState(BRIDESMAIDS_GARMENT_OPTIONS[0]);

  const handleSubmit = () => {
    if (generating) return;
    onSubmit({
      wedding_theme: WEDDING_PALETTE_THEMES.find(t => t.value === theme)?.label || theme,
      season,
      groom_color: groomColor.hex,
      groom_color_name: groomColor.name,
      groom_garment: groomGarment,
      groom_style: groomStyle,
      groomsmen_color: groomsmenColor.hex,
      groomsmen_color_name: groomsmenColor.name,
      groomsmen_garment: groomsmenGarment,
      bridesmaids_color: bridesmaidsColor.hex,
      bridesmaids_color_name: bridesmaidsColor.name,
      bridesmaids_garment: bridesmaidsGarment,
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-3xl bg-[#0A0A0C] border border-[#27272A] rounded-none p-0 max-h-[90vh] overflow-y-auto"
        data-testid="wedding-questionnaire-modal"
      >
        <DialogHeader className="px-6 py-5 border-b border-[#27272A] sticky top-0 bg-[#0A0A0C] z-10">
          <DialogTitle className="text-2xl font-light text-[#F5F5F5]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Plan Wedding Party
          </DialogTitle>
          <DialogDescription className="text-xs text-[#A1A1AA] mt-1">
            Three renders · groom (with face) · groomsmen + bridesmaids on designer mannequins. Tie & shoe palette suggestions included.
          </DialogDescription>
        </DialogHeader>

        <div className="p-6 space-y-6">

          {/* Wedding theme / palette */}
          <section>
            <Label className="label-luxury mb-2 block">Wedding Theme</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {WEDDING_PALETTE_THEMES.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setTheme(opt.value)}
                  className={`p-3 border text-left transition-all ${theme === opt.value ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10' : 'border-[#27272A] hover:border-[#52525B]'}`}
                  data-testid={`theme-${opt.value}`}
                >
                  <p className="text-sm text-[#F5F5F5]">{opt.label}</p>
                  <p className="text-[10px] text-[#A1A1AA] mt-0.5">{opt.hint}</p>
                </button>
              ))}
            </div>
          </section>

          {/* Groom */}
          <section className="pt-2 border-t border-[#27272A]">
            <Label className="label-luxury mb-2 block">Groom · style</Label>
            <div className="flex flex-wrap gap-2 mb-3">
              {GROOM_STYLE_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setGroomStyle(opt.value)}
                  className={`px-3 py-1.5 text-xs border transition-all ${groomStyle === opt.value ? 'border-[hsl(var(--accent))] bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]' : 'border-[#27272A] text-[#A1A1AA] hover:border-[#52525B]'}`}
                  data-testid={`groom-style-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <Label className="label-luxury mb-2 block">Groom · suit colour</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-3">
              {SUIT_COLOR_OPTIONS.map(opt => (
                <PaletteSwatch
                  key={opt.name}
                  option={opt}
                  selected={groomColor.name === opt.name}
                  onClick={() => setGroomColor(opt)}
                />
              ))}
            </div>

            <Label className="label-luxury mb-2 block">Groom · garment</Label>
            <select
              value={groomGarment}
              onChange={(e) => setGroomGarment(e.target.value)}
              className="w-full bg-[#121215] border border-[#27272A] text-[#F5F5F5] text-sm py-2 px-3 rounded-none"
              data-testid="groom-garment-select"
            >
              {GROOM_GARMENT_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </section>

          {/* Groomsmen */}
          <section className="pt-2 border-t border-[#27272A]">
            <Label className="label-luxury mb-2 block">Groomsmen · suit colour</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mb-3">
              {SUIT_COLOR_OPTIONS.map(opt => (
                <PaletteSwatch
                  key={opt.name}
                  option={opt}
                  selected={groomsmenColor.name === opt.name}
                  onClick={() => setGroomsmenColor(opt)}
                />
              ))}
            </div>

            <Label className="label-luxury mb-2 block">Groomsmen · suit type</Label>
            <select
              value={groomsmenGarment}
              onChange={(e) => setGroomsmenGarment(e.target.value)}
              className="w-full bg-[#121215] border border-[#27272A] text-[#F5F5F5] text-sm py-2 px-3 rounded-none"
              data-testid="groomsmen-garment-select"
            >
              {GROOMSMEN_GARMENT_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </section>

          {/* Bridesmaids */}
          <section className="pt-2 border-t border-[#27272A]">
            <Label className="label-luxury mb-2 block">Bridesmaids · dress colour</Label>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-3">
              {DRESS_COLOR_OPTIONS.map(opt => (
                <PaletteSwatch
                  key={opt.name}
                  option={opt}
                  selected={bridesmaidsColor.name === opt.name}
                  onClick={() => setBridesmaidsColor(opt)}
                />
              ))}
            </div>

            <Label className="label-luxury mb-2 block">Bridesmaids · dress type</Label>
            <select
              value={bridesmaidsGarment}
              onChange={(e) => setBridesmaidsGarment(e.target.value)}
              className="w-full bg-[#121215] border border-[#27272A] text-[#F5F5F5] text-sm py-2 px-3 rounded-none"
              data-testid="bridesmaids-garment-select"
            >
              {BRIDESMAIDS_GARMENT_OPTIONS.map(g => <option key={g} value={g}>{g}</option>)}
            </select>
          </section>

          {/* Submit */}
          <div className="pt-4 border-t border-[#27272A] flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <div className="flex gap-2 items-center">
              <span className="text-[10px] text-[#52525B] uppercase tracking-wider">Preview:</span>
              <div className="flex gap-1">
                <div className="w-6 h-6 border border-[#27272A]" style={{ backgroundColor: groomsmenColor.hex }} title={`Groomsmen · ${groomsmenColor.name}`} />
                <div className="w-6 h-6 border-2 border-[hsl(var(--accent))]" style={{ backgroundColor: groomColor.hex }} title={`Groom · ${groomColor.name}`} />
                <div className="w-6 h-6 border border-[#27272A]" style={{ backgroundColor: bridesmaidsColor.hex }} title={`Bridesmaids · ${bridesmaidsColor.name}`} />
              </div>
            </div>
            <Button
              onClick={handleSubmit}
              disabled={generating}
              className="rounded-none uppercase tracking-wider text-xs px-6 py-5 bg-[hsl(var(--accent))] text-[#0A0A0C] hover:bg-[hsl(var(--accent))]/90"
              data-testid="wedding-questionnaire-submit"
            >
              {generating ? (<><Loader2 className="w-4 h-4 spinner mr-2" /> Generating Trio...</>) : (<><Sparkles className="w-4 h-4 mr-2" /> Generate Wedding Party</>)}
            </Button>
          </div>

        </div>
      </DialogContent>
    </Dialog>
  );
}
